// src/hooks/useReturns.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PurchaseOrderReturnService } from '../admin/pages/purchase-orders/purchaseOrderReturnService';
import { ReturnStatus } from '../admin/pages/purchase-orders/PurchaseOrder';

export const RETURN_KEYS = {
  all: ['returns'] as const,
  lists: () => ['returns', 'list'] as const,
  paginated: (page: number, search: string, status: string) =>
    ['returns', 'list', page, search, status] as const,
  stats: () => ['returns', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface ReturnPaginationParams {
  page: number;
  search: string;
  status: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getReturnsPaginated instead of
// cursor objects. Any page can now be fetched in any order.
export const useReturnsPaginated = (params: ReturnPaginationParams) => {
  return useQuery({
    queryKey: RETURN_KEYS.paginated(params.page, params.search, params.status),
    queryFn: () =>
      PurchaseOrderReturnService.getReturnsPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        params.status !== 'all' ? (params.status as ReturnStatus) : undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const useReturnStats = () => {
  return useQuery({
    queryKey: RETURN_KEYS.stats(),
    queryFn: PurchaseOrderReturnService.getReturnStats.bind(PurchaseOrderReturnService),
    staleTime: 1000 * 60 * 5,
  });
};

export const useReturnMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: RETURN_KEYS.all });
  };

  const remove = useMutation({
    mutationFn: (returnId: string) => PurchaseOrderReturnService.deleteReturn(returnId),
    onSuccess: invalidateAll,
  });

  const updateStatus = useMutation({
    mutationFn: ({
      returnId,
      status,
      userId,
      userName,
    }: {
      returnId: string;
      status: ReturnStatus;
      userId: string;
      userName: string;
    }) => PurchaseOrderReturnService.updateReturnStatus(returnId, status, userId, userName),
    onSuccess: invalidateAll,
  });

  const complete = useMutation({
    mutationFn: ({
      returnId,
      refundAmount,
      userId,
      userName,
    }: {
      returnId: string;
      refundAmount: number;
      userId: string;
      userName: string;
    }) =>
      PurchaseOrderReturnService.completeReturnWithInventoryAdjustment(
        returnId,
        refundAmount,
        userId,
        userName
      ),
    onSuccess: invalidateAll,
  });

  return { remove, updateStatus, complete };
};